import AppContext from "../../../framework/AppContext";
import AppBar from "../../../framework/component/frame/AppBar";
import React, {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import SignUpModel from "./model/SignUpModel";
import PageHeader from "../../../framework/component/frame/PageHeader";
import MessagePanel from "../../../framework/component/container/MessagePanel";
import FormPanel from "../../../framework/component/form/FormPanel";

import "./SignUp.css";
import ReCAPTCHA from "react-google-recaptcha";
import SignUpForm from "./SignUpForm";
import Form from "react-bootstrap/Form";
import {COMMON_HEADER} from "../NewResearcherConstants";
import {MessagePanelType} from "../../../framework/component/container/MessagePanelType";
import {useLocation, useNavigate} from "react-router-dom";
import axios, {AxiosError, AxiosRequestConfig} from "axios";
import apiClient from "../../../framework/web/ApiClient";

/**
 * The extra information we will be showing in the header
 */
const TITLE_SECTION_CONTENT = <>
  {COMMON_HEADER}
</>;

/**
 * The URL the cancel btn will redirect to
 */
const REDIRECT_URL = "/ams";

/**
 * URL for submit request
 */
const UI_API_PATH = "/ams/ui-api/v1/researchers/signup";

/**
 * Needed for errors containing field ID
 */
type SignUpModelField = keyof SignUpModel | "root";

/**
 * This is the main component that defines the sign-up page.
 */
export default function SignUp() {
  const [isRecaptchaEnabled, setIsRecaptchaEnabled] = useState<boolean>(true);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string>("");
  const signupKey = new URLSearchParams(useLocation().search).get("key");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: {errors, isSubmitting},
    setError,
    clearErrors,
    trigger,
  } = useForm<SignUpModel>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
      recaptcha: null
    }
  });

  let context!: React.ContextType<typeof AppContext>;

  const navigate = useNavigate();

  // Keep track of whether we have an initial email value or not (ie. access through link)
  const [hasInitialEmail, setHasInitialEmail] = useState<boolean>(false);
  /**
   * Make initial API call to load the data in redis (in case this was open via invitation link) and
   * necessary system preferences (TODO Implement this in future ticket)
   */
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await apiClient.get(UI_API_PATH, { params: { key: signupKey }});

        setIsRecaptchaEnabled(response.data.data.recaptchaEnabled);
        setRecaptchaSiteKey(response.data.data.recaptchaSiteKey ?? "");
        if (response.status === 200 && response.data.data.email) {
          setHasInitialEmail(true);
          // Pre set email
          setValue("email", response.data.data.email);
          setValue("username", response.data.data.email.split("@")[0]);
        }
      } catch (error: AxiosError | any) {
        // If we get a 400 it falls here so we need to determine if we need to redirect somewhere
        if (!axios.isAxiosError(error)) {
          console.warn(`There was an error with the initial API call (error = ${error})`);
          return;
        }

        switch (error.response?.data.errors[0].code) {
          case "132001":
            navigate("/signup/expired-link");
            break;
          case "132002":
            navigate("/signup/already-signed-up");
            break;
        }
      }
    }
    fetchFormData();
  }, [setValue]);

  /**
   * Method to handle the submission of the form
   */
  const onSubmit = async (data: SignUpModel, e: any) => {
    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    // Clear previous errors
    clearErrors("root");

    try {
      const headers: AxiosRequestConfig = {
        headers: {
          "Content-Type": "application/json",
        }
      };
      const response = await apiClient.post(UI_API_PATH, {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        username: data.username,
        password: data.password,
        recaptcha: data.recaptcha
      }, headers);
      if (response.status === 201) {
        navigate("/signup/success");
      } else {
        response.data.errors?.forEach(({code, message, fieldId}: {
          code: string,
          message: string,
          fieldId: SignUpModelField
        }) => {
          // If field ID empty or null then it's a root error
          const errorFieldId = (!fieldId) ? "root" : fieldId;
          setError(errorFieldId, {type: "server", message: message});
        })
        console.warn(`Error when submitting sign up form. (status: ${response.status}, body: ${response.statusText})`);
        scrollToTop();
      }
    } catch (error: AxiosError | any) {
      // Non expected error
      if (!axios.isAxiosError(error)) {
        console.warn(`Error when submitting sign up form. (error = ${error})`);
        setError("root", {type: "server", message: "Unexpected error when signing up. Please try again later."});
        scrollToTop();
        return;
      }

      // API call error
      error.response?.data.errors?.forEach(({code, message, field}: {
        code: string,
        message: string,
        field: SignUpModelField
      }) => {
        // If field ID empty or null then it's a root error
        const errorFieldId = (!field) ? "root" : field;
        setError(errorFieldId, {type: "server", message: message});
      })
      console.warn(`Error when submitting sign up form. (status: ${error.response?.status}, body: ${error.response?.statusText})`);
      scrollToTop();
    }
  }

  // These are to be used to scroll to top in case of error so user can see the error panel
  const singUpContentRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    singUpContentRef.current?.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };


  return <div id={"signUpContentPage"} className="page">
    <AppContext.Provider value={context}>
      <AppBar/>
      <PageHeader title={"Sign Up"}>{TITLE_SECTION_CONTENT}</PageHeader>
      <div className={"content-area non-menu-page"} ref={singUpContentRef}>
        {/* Privacy message */}
        <MessagePanel title={"Privacy"}>
          <p>Please note - UK Biobank will process your personal data in accordance with the UK
            Biobank Privacy Policy available at <a
                href={"https://www.ukbiobank.ac.uk/privacy-policy"}>https://www.ukbiobank.ac.uk/privacy-policy</a>.
          </p>
        </MessagePanel>
        {/* Form */}
        <Form id={"signUpForm"} className={"signUpForm"}
              onSubmit={handleSubmit(onSubmit)} noValidate data-testid="sign-up-form">
          {/*If any unexpected error occurs*/}
          {errors.root && (
              <MessagePanel title={"Unexpected error"} panelType={MessagePanelType.ERROR} data-testid="root-error">
                {errors.root.message}
              </MessagePanel>
          )}
          <FormPanel title={"Sign Up"}>
            <SignUpForm
                hasInitialEmail={hasInitialEmail}
                register={register}
                errors={errors}
                watch={watch}
                setError={setError}
                setValue={setValue}
                trigger={trigger}/>
          </FormPanel>
          {/*Add ReCAPTCHA when a key is found*/}
          {recaptchaSiteKey && isRecaptchaEnabled && (
              <Form.Group className="mb-3" controlId="recaptcha">
                <Form.Control
                    type="text"
                    hidden
                    isInvalid={!!errors.recaptcha}
                    {...register("recaptcha", {
                      required: "Please verify you are not a robot."
                    })}
                />
                <ReCAPTCHA sitekey={recaptchaSiteKey}
                           onChange={(token) =>
                               setValue("recaptcha", token, {
                                 shouldValidate: true,
                                 shouldDirty: true
                               })}
                           data-testid="recaptcha"/>
                <Form.Control.Feedback type="invalid" data-testid="recaptcha-error">
                  {errors.recaptcha?.message}
                </Form.Control.Feedback>
              </Form.Group>
          )}
          {/* Submit & Cancel buttons*/}
          <div id={"signUpBtnArea"} className={"btnArea"}>
            <button id="singUpSubmitBtn" type="submit" className="btn btn-primary"
                    data-testid="submit" disabled={isSubmitting}>Submit
            </button>
            <a id="signUpCancelBtn" href={REDIRECT_URL} className="btn btn-outline-secondary">Cancel</a>
          </div>
        </Form>
      </div>
    </AppContext.Provider>
  </div>
      ;
}